import { useQuery } from "@tanstack/react-query";
import { getApiBase, withPartNumberSkuQuery } from "../helpers/helpers";
import { withIDBFallback, IDB_STORES, IDB_TTL } from "./IDBCacheService";

// Mirrors app/Http/Controllers/Api/PublicApi/HolidayController.php in
// scsbiz3.wearevisum.com: GET /api/v1/public/holidays returns upcoming
// (today onward) non-working days as { data: [{ date, name }, ...] },
// server-cached for 1 hour. We only need the 'YYYY-MM-DD' date strings to
// skip in addBusinessDays.

const HOLIDAYS_STALE_TIME = 1000 * 60 * 60; // 1 hour — matches the API's own cache
const HOLIDAYS_CACHE_TIME = 1000 * 60 * 60 * 6; // 6 hours

export const getHolidaysQueryKey = () => ["holidays"];

export const fetchHolidays = async (): Promise<string[]> => {
  return withIDBFallback(
    IDB_STORES.HOLIDAYS,
    "upcoming",
    IDB_TTL.HOLIDAYS,
    async () => {
      const response = await fetch(withPartNumberSkuQuery(`${getApiBase()}/api/v1/public/holidays`), {
        mode: "cors",
        headers: {
          "Cache-Control": "public, max-age=3600" // 1 hour browser cache
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch holidays: ${response.statusText}`);
      }

      const json = await response.json();
      const data: { date: string }[] = json.data ?? [];
      return data.map((h) => h.date);
    }
  );
};

export const useHolidaysQuery = () => {
  return useQuery<string[]>({
    queryKey: getHolidaysQueryKey(),
    queryFn: fetchHolidays,
    staleTime: HOLIDAYS_STALE_TIME,
    gcTime: HOLIDAYS_CACHE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Prefetch function for early loading, mirrors prefetchYears in YearsService.
export const prefetchHolidays = async (queryClient: any) => {
  await queryClient.prefetchQuery({
    queryKey: getHolidaysQueryKey(),
    queryFn: fetchHolidays,
    staleTime: HOLIDAYS_STALE_TIME,
    gcTime: HOLIDAYS_CACHE_TIME,
  });
};
